import { datadogRum } from '@datadog/browser-rum';
import '@salesforce/platform-sdk/ui-embedding';
// Ensures the OAuth redirect is caught on a fresh page load, not just mid-session.
import 'aws-amplify/auth/enable-oauth-listener';
import { IconSettings, Settings } from '@salesforce/design-system-react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';

import { App } from './App';
import { AuthGate } from './app/auth/AuthGate';
import { configureAmplify } from './app/bootstrap/configureAmplify';
import './theme.css';

declare const __APP_VERSION__: string;

configureAmplify();

if (import.meta.env.PROD) {
  datadogRum.init({
    allowedTracingUrls: [import.meta.env.VITE_API_BASE_URL],
    applicationId: import.meta.env.VITE_DD_APPLICATION_ID ?? 'placeholder',
    clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN ?? 'placeholder',
    defaultPrivacyLevel: 'mask-user-input',
    env: import.meta.env.VITE_DD_ENV ?? 'local',
    service: import.meta.env.VITE_DD_SERVICE ?? 'mkf',
    // Vite env vars are always strings at runtime — Number() converts correctly
    sessionReplaySampleRate: Number(import.meta.env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE ?? '0'),
    sessionSampleRate: 100,
    site: 'datadoghq.com',
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version: __APP_VERSION__,
  });

  datadogRum.startSessionReplayRecording();
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

Settings.setAppElement(rootElement);
createRoot(rootElement).render(
  <StrictMode>
    <IconSettings iconPath="/assets/icons">
      <ErrorBoundary
        FallbackComponent={props => (
          <div className="slds-p-around_large" role="alert">
            <h1 className="slds-text-heading_medium">Oops! Something went wrong</h1>
            <p className="slds-m-top_small">{props.error?.toString()}</p>
          </div>
        )}
      >
        <AuthGate>
          <App />
        </AuthGate>
      </ErrorBoundary>
    </IconSettings>
  </StrictMode>,
);
